import React from 'react';
import { User } from '../../api/structs/user';

interface ClipTelegramActionsProps {
  hasVideoOutput: boolean;
  activeUser: User | null;
  isSendingTelegram: boolean;
  /**
   * Which phase the background send is in ("Compressing…", "Uploading…"). The
   * button is icon-only, so this is the only place the user can see that a
   * 70-second transcode is under way rather than a stuck click.
   */
  telegramSendLabelText?: string;
  onSendToTelegram: () => void;
  isCarouselClip: boolean;
  /** Slides the carousel send would post (video slides included). */
  orderedImagePathsCount: number;
  isSendingCarousel: boolean;
  onSendCarouselToTelegram: () => void;
}

const TelegramIconPath = () => (
  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
);

const buttonClass = `px-4 py-3 bg-black border border-white/20 hover:border-white/50
  text-white transition-all active:scale-[0.97]
  disabled:opacity-50 disabled:cursor-not-allowed`;

/**
 * Telegram distribution shortcuts for a rendered clip. Split out of the render
 * button so they live under the "Publish" section while keeping their original
 * per-output conditionals intact.
 */
const ClipTelegramActions: React.FC<ClipTelegramActionsProps> = ({
  hasVideoOutput,
  activeUser,
  isSendingTelegram,
  telegramSendLabelText,
  onSendToTelegram,
  isCarouselClip,
  orderedImagePathsCount,
  isSendingCarousel,
  onSendCarouselToTelegram,
}) => {
  // A carousel's video slides go out with the rest of the set; the single-video
  // send would post only the first of them.
  const showVideo = hasVideoOutput && activeUser && !isCarouselClip;
  const showCarousel = isCarouselClip && orderedImagePathsCount > 0 && activeUser;
  if (!showVideo && !showCarousel) return null;

  return (
    <div className="flex gap-2">
      {showVideo && (
        <button
          onClick={onSendToTelegram}
          disabled={isSendingTelegram}
          title={telegramSendLabelText || 'Send to Telegram'}
          aria-label={telegramSendLabelText || 'Send to Telegram'}
          className={`${buttonClass} ${isSendingTelegram ? 'animate-pulse' : ''}`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <TelegramIconPath />
          </svg>
        </button>
      )}
      {showCarousel && (
        <button
          onClick={onSendCarouselToTelegram}
          disabled={isSendingCarousel}
          title="Send carousel to Telegram"
          className={`${buttonClass} ${isSendingCarousel ? 'animate-pulse' : ''}`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <TelegramIconPath />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ClipTelegramActions;
